import { useEffect, useState } from "react";
import { FaPaperclip, FaPlus, FaArchive, FaDownload, FaChevronDown, FaChevronRight, FaExpand } from "react-icons/fa";
import { LOGBOOK_CONSTANTS, DOCUMENTS_CONSTANTS, CHANGES_CONSTANTS } from "../_MODULES/_API";
import { TAGS_CONSTANTS } from "../../_Tags/_MODULES/_API";
import { OPTIONS_LOGBOOK_TYPE, OPTIONS_LOGBOOK_STATUS, OPTIONS_PRIORITY, OPTIONS_CODE_LANGUAGE, OPTIONS_CHANGE_ACTION } from "../../../_Catalogs.Views";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FormGrid, FormSection, FieldInput, FieldTextarea, FieldSelect, FieldSwitch, FieldDate, FieldCode, FieldCheckboxGroup, FieldFile, useForm } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { onApiAction } from "../../../../_UI/onApiAction";
import { API_CORE } from "../../../../../_CORE/API/ApiCORE";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { UniversalFilePreviewModal } from "../../../../_UI/UniversalFilePreview";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";

const EMPTY_CHANGE = { filePath: "", language: "typescript", action: "MODIFIED", description: "", codeBefore: "", codeAfter: "" };

const EMPTY_VALUES = {
    title: "", type: "LOG", status: "OPEN", priority: "MEDIUM",
    specification: "", problem: "", solution: "", content: "",
    code: "", codeLanguage: "bash", dueDate: null, isPinned: false, tagCodes: [],
};

export const LogbookFormModal = ({ isOpen, subProjectId, entry = null, onClose, onSaved }) => {

    const IS_EDIT = !!entry?.id;

    const [useFiles, setFiles] = useState([]);
    const [useExistingFiles, setExistingFiles] = useState([]);
    const [useChanges, setChanges] = useState([]);
    const [useExpandedChanges, setExpandedChanges] = useState({});
    const [useIsUploading, setIsUploading] = useState(false);
    const [usePreviewFile, setPreviewFile] = useState(null);

    const { apiGet: onGetTags, useApiData: TAGS } = useApiQuery({ minTimeQuery: 200, enablePagination: false });

    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 300,
    });

    const onChangeRow = (index, field, value) => {
        setChanges((prev) => prev.map((row, position) => (
            position === index ? { ...row, [field]: value } : row
        )));
    };

    const onAddChange = () => setChanges((prev) => [...prev, { ...EMPTY_CHANGE }]);

    const onRemoveChange = async (index) => {
        const ROW = useChanges[index];
        if (!ROW) return;

        if (ROW.id) {
            const OK = await confirm.danger("Archivar cambio de código", `Se archiva el cambio de "${ROW.filePath || "este fichero"}".`);
            if (!OK) return;
            const RESPONSE = await onApiAction({
                method: "PATCH", endpoint: CHANGES_CONSTANTS.ARCHIVE_ONE,
                pathParams: { id: ROW.id }, successMessage: "Cambio de código archivado",
            });
            if (!RESPONSE.success) return;
        }

        setChanges((prev) => prev.filter((row, position) => position !== index));
    };

    const onSaveChanges = async (logbookId) => {

        const VALID = useChanges.filter((row) => !!row.filePath.trim());
        const SAVED = [];

        for (const row of VALID) {

            const PAYLOAD = {
                filePath: row.filePath.trim(),
                language: row.language,
                action: row.action,
                description: row.description || "",
                codeBefore: row.codeBefore || "",
                codeAfter: row.codeAfter || "",
            };

            if (row.id) {
                const response = await API_CORE.SERVICES.PATCH_resource(API_CORE.ENDPOINTS[CHANGES_CONSTANTS.UPDATE], row.id, PAYLOAD);
                if (response?.success === false) continue;
                SAVED.push({ ...row, ...(response?.data ?? {}), ...PAYLOAD });
                continue;
            }

            const response = await API_CORE.SERVICES.POST_RESOURCE(
                API_CORE.ENDPOINTS[CHANGES_CONSTANTS.CREATE], { logbookId, ...PAYLOAD },
            );
            if (response?.success === false) continue;
            SAVED.push(response?.data ?? { logbookId, ...PAYLOAD });
        }

        return SAVED;
    };

    const onUploadFiles = async (logbookId) => {

        if (useFiles.length === 0) return [];

        setIsUploading(true);

        const FORM_DATA = new FormData();
        FORM_DATA.append("subProjectId", subProjectId);
        FORM_DATA.append("logbookId", logbookId);
        FORM_DATA.append("title", "Adjunto");
        useFiles.forEach((file) => FORM_DATA.append("files[]", file));

        const response = await API_CORE.SERVICES.POST_RESOURCE(
            API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.UPLOAD_MULTIPLE], FORM_DATA,
        );

        setIsUploading(false);
        if (response?.success === false) return [];
        setFiles([]);
        return Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    };

    const onLoadExistingFileBlob = async (document) => {
        if (!document?.id) throw new Error("Adjunto sin identificador");
        const url = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.CONTENT].replace(":id", document.id);
        const blob = await API_CORE.SERVICES.GET_RESOURCE(url, { responseType: "blob" });
        if (!(blob instanceof Blob)) throw new Error("No se pudo obtener el adjunto");
        return blob;
    };

    const onDownloadExistingFile = async (document) => {
        if (!document?.id) return;
        const url = API_CORE.ENDPOINTS[DOCUMENTS_CONSTANTS.DOWNLOAD].replace(":id", document.id);
        const blob = await API_CORE.SERVICES.GET_RESOURCE(url, { responseType: "blob" });
        if (!(blob instanceof Blob)) return;
        const objectUrl = window.URL.createObjectURL(blob);
        const link = window.document.createElement("a");
        link.href = objectUrl;
        link.download = document.originalName || document.title || "archivo";
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    };

    const onDeleteExistingFile = async (document) => {
        const ok = await confirm.danger("Archivar adjunto", `Se archiva "${document.originalName || document.title}" de esta entrada.`);
        if (!ok) return;
        const response = await onApiAction({
            method: "PATCH", endpoint: DOCUMENTS_CONSTANTS.ARCHIVE_ONE,
            pathParams: { id: document.id }, successMessage: "Adjunto archivado",
        });
        if (response.success) setExistingFiles((prev) => prev.filter((item) => item.id !== document.id));
    };

    const onToggleChange = (key) => setExpandedChanges((prev) => ({ ...prev, [key]: !prev[key] }));

    const FORM = useForm({
        initialValues: EMPTY_VALUES,

        schema: { title: { required: true, minLength: 2 } },

        onSubmit: async (values) => {

            const PAYLOAD = {
                subProjectId,
                title: values.title.trim(),
                type: values.type,
                status: values.status,
                priority: values.priority,
                specification: values.specification || undefined,
                problem: values.problem || undefined,
                solution: values.solution || undefined,
                content: values.content || undefined,
                code: values.code || undefined,
                codeLanguage: values.codeLanguage,
                dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : undefined,
                isPinned: values.isPinned,
                tagCodes: values.tagCodes?.length > 0 ? values.tagCodes : undefined,
            };

            const RESPONSE = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: LOGBOOK_CONSTANTS.UPDATE, successMessage: "Entrada actualizada", id: entry.id, data: PAYLOAD })
                : await actionMutate({ method: "POST", endpoint: LOGBOOK_CONSTANTS.CREATE, successMessage: "Entrada agregada a la bitácora", data: PAYLOAD });

            if (!RESPONSE.success) { FORM.applyApiError(RESPONSE); return; }

            const LOGBOOK_ID = RESPONSE.data?.id ?? entry?.id;

            const SAVED_CHANGES = await onSaveChanges(LOGBOOK_ID);
            const UPLOADED_FILES = await onUploadFiles(LOGBOOK_ID);

            // Construimos el estado final en memoria. No hacemos un GET extra
            // sólo para volver a leer lo que acabamos de guardar.
            const LOCAL_ENTRY = {
                ...(entry ?? {}),
                ...(RESPONSE.data ?? {}),
                id: LOGBOOK_ID,
                changes: SAVED_CHANGES,
                documents: [...useExistingFiles, ...UPLOADED_FILES],
            };

            onSaved?.(LOCAL_ENTRY, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {

        if (!isOpen) return;

        onGetTags({ endpoint: TAGS_CONSTANTS.LIST });

        setFiles([]);
        setExistingFiles(IS_EDIT ? (entry.documents ?? []).map((document) => ({ ...document })) : []);
        setChanges(IS_EDIT ? (entry.changes ?? []).map((change) => ({ ...change })) : []);
        setExpandedChanges({});

        FORM.setValues(IS_EDIT ? {
            title: entry.title ?? "",
            type: entry.type ?? "LOG",
            status: entry.status ?? "OPEN",
            priority: entry.priority ?? "MEDIUM",
            specification: entry.specification ?? "",
            problem: entry.problem ?? "",
            solution: entry.solution ?? "",
            content: entry.content ?? "",
            code: entry.code ?? "",
            codeLanguage: entry.codeLanguage ?? "bash",
            dueDate: entry.dueDate ? new Date(entry.dueDate) : null,
            isPinned: entry.isPinned ?? false,
            tagCodes: (entry.tags ?? []).map((tag) => tag.code),
        } : EMPTY_VALUES);

    }, [isOpen, entry]); // eslint-disable-line react-hooks/exhaustive-deps

    const TAG_OPTIONS = (TAGS ?? []).map((tag) => ({ value: tag.code, label: tag.name }));

    return (
        <>
        <AppModal
            isOpen={isOpen}
            onClose={onClose}
            isDirty={FORM.isDirty || useFiles.length > 0}
            size="xl"
            title={IS_EDIT ? "Editar entrada" : "Nueva entrada de bitácora"}
            description="Cargá la especificación, el problema, la solución, el código y los adjuntos."
            footer={
                <>
                    <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                    <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading || useIsUploading}>
                        {IS_EDIT ? "Guardar cambios" : "Crear entrada"}
                    </AppButton>
                </>
            } >

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="isPinned" label="Destacado" text="Mostrar al inicio del listado"
                    isChecked={FORM.values.isPinned}
                    onChange={(event) => FORM.setValue("isPinned", event.target.checked)} />
            </div>

            <FormGrid columns={3}>

                <FieldInput {...FORM.field("title")} label="Título" placeholder="Ej: Arreglar el login" />

                <FieldSelect {...FORM.field("type")} label="Tipo" options={OPTIONS_LOGBOOK_TYPE} isSearchable />

                <FieldSelect {...FORM.field("status")} label="Estado" options={OPTIONS_LOGBOOK_STATUS} isSearchable />

                <FieldSelect {...FORM.field("priority")} label="Prioridad" options={OPTIONS_PRIORITY} isSearchable />

                <FieldDate {...FORM.fieldValue("dueDate", { emptyValue: null })} label="Fecha límite" />

                <FieldTextarea {...FORM.field("specification")} label="Especificación" rows={3}
                    placeholder="Qué hay que hacer y con qué criterio" span={3} />

                <FieldTextarea {...FORM.field("problem")} label="Problema" rows={3}
                    placeholder="Qué falló o qué te trabó" span={3} />

                <FieldTextarea {...FORM.field("solution")} label="Solución" rows={3}
                    placeholder="Cómo lo resolviste" span={3} />

                <FieldTextarea {...FORM.field("content")} label="Notas" rows={3} span={3} placeholder="Ej: Puntos de la reunion" />

                <FieldSelect {...FORM.field("codeLanguage")} label="Lenguaje del código" options={OPTIONS_CODE_LANGUAGE} isSearchable />

                <FieldCode {...FORM.field("code")} label="Código" language={FORM.values.codeLanguage}
                    rows={8} placeholder="npm run start:dev" span={3} />

                <FieldCheckboxGroup {...FORM.fieldValue("tagCodes", { emptyValue: [] })}
                    label="Etiquetas" options={TAG_OPTIONS} direction="grid" span={3} />

                <FieldFile id="files" label="Adjuntar imágenes o documentos" files={useFiles}
                    onChange={setFiles} maxFiles={20} dropText="Arrastrá varias capturas o documentos" span={3} />

                {IS_EDIT && useExistingFiles.length > 0 && (
                    <div className="LStudio__app_logbook_existing_files" style={{ gridColumn: "1 / -1" }}>
                        <div className="LStudio__app_logbook_existing_files_head">
                            <FaPaperclip />
                            <strong>Adjuntos ya guardados ({useExistingFiles.length})</strong>
                            <span>Los nuevos archivos se suman; podés descargar o archivar los anteriores.</span>
                        </div>
                        <div className="LStudio__app_logbook_existing_files_list">
                            {useExistingFiles.map((document) => (
                                <div key={document.id} className="LStudio__app_logbook_existing_file">
                                    <span className="LStudio__app_logbook_existing_file_name">
                                        <strong>{document.originalName || document.title}</strong>
                                        <small>{document.mimetype || "Archivo"}{document.size ? ` · ${(Number(document.size) / 1024).toFixed(0)} KB` : ""}</small>
                                    </span>
                                    <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly icon={<FaExpand />}
                                        aria-label="Vista previa" title="Vista previa" onClick={() => setPreviewFile(document)} />
                                    <CardActionMenu title="Acciones del adjunto" items={[
                                        { key: "download", icon: <FaDownload />, label: "Descargar", onClick: () => onDownloadExistingFile(document) },
                                        { divider: true },
                                        { key: "delete", icon: <FaArchive />, label: "Archivar", tone: "danger", onClick: () => onDeleteExistingFile(document) },
                                    ]} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </FormGrid>

            <FormSection title="Cambios de código" columns={1} className="LStudio__app_logbook_changes_section"
                description="Qué fichero tocaste, cómo estaba antes y cómo quedó. Podés sumar varios.">

                <div className="LStudio__app_logbook_changes_intro">
                    <strong>{useChanges.length ? `${useChanges.length} cambio${useChanges.length === 1 ? "" : "s"} cargado${useChanges.length === 1 ? "" : "s"}` : "Todavía no agregaste cambios"}</strong>
                    <span>Cada cambio ocupa todo el ancho para que fichero, motivo y código no queden apretados.</span>
                </div>

                <div className="LStudio__app_change_rows">
                    {useChanges.map((row, index) => {
                        const rowKey = row.id ?? index;
                        const isCollapsed = useExpandedChanges[rowKey] === false;
                        return (
                            <div key={rowKey} className="LStudio__app_change_row" data-collapsed={isCollapsed}>
                                <div className="LStudio__app_change_row_head">
                                    <FieldInput id={`change_file_${index}`} label="Fichero modificado"
                                        placeholder="Ej: src/Modules/project.service.ts" value={row.filePath}
                                        onChange={(event) => onChangeRow(index, "filePath", event.target.value)} />

                                    <FieldSelect id={`change_action_${index}`} label="Acción" options={OPTIONS_CHANGE_ACTION} isSearchable
                                        value={row.action}
                                        onChange={(event) => onChangeRow(index, "action", event.target.value)} />

                                    <FieldSelect id={`change_lang_${index}`} label="Lenguaje" options={OPTIONS_CODE_LANGUAGE} isSearchable
                                        value={row.language}
                                        onChange={(event) => onChangeRow(index, "language", event.target.value)} />

                                    <div className="LStudio__app_change_row_head_actions">
                                        <AppButton size="sm" variant="ghost" tone="neutral" isIconOnly
                                            icon={isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                                            aria-label={isCollapsed ? "Expandir cambio" : "Contraer cambio"}
                                            onClick={() => onToggleChange(rowKey)} />
                                        <AppButton size="sm" variant="ghost" tone="danger" icon={<FaArchive />} isIconOnly
                                            aria-label="Quitar cambio" onClick={() => onRemoveChange(index)} />
                                    </div>
                                </div>

                                {!isCollapsed && (
                                    <div className="LStudio__app_change_row_detail">
                                        <FieldInput id={`change_desc_${index}`} label="Por qué lo cambiaste"
                                            placeholder="Ej: La validación corría tarde" value={row.description}
                                            onChange={(event) => onChangeRow(index, "description", event.target.value)} />

                                        <div className="LStudio__app_change_row_codes">
                                            <FieldCode id={`change_before_${index}`} label="Código anterior"
                                                language={row.language} rows={9} value={row.codeBefore}
                                                placeholder="Ej: await save(x)"
                                                onChange={(event) => onChangeRow(index, "codeBefore", event.target?.value ?? event)} />

                                            <FieldCode id={`change_after_${index}`} label="Código nuevo"
                                                language={row.language} rows={9} value={row.codeAfter}
                                                placeholder="Ej: validate(x); await save(x)"
                                                onChange={(event) => onChangeRow(index, "codeAfter", event.target?.value ?? event)} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="LStudio__app_logbook_changes_add">
                    <AppButton variant="soft" tone="neutral" icon={<FaPlus />} onClick={onAddChange}>
                        {useChanges.length ? "Agregar otro cambio de código" : "Agregar cambio de código"}
                    </AppButton>
                </div>

            </FormSection>

        </AppModal>

        <UniversalFilePreviewModal
            file={usePreviewFile}
            isOpen={!!usePreviewFile}
            onClose={() => setPreviewFile(null)}
            loadBlob={onLoadExistingFileBlob}
            onDownload={onDownloadExistingFile}
        />
        </>
    );
};
